import { useState } from "react";
import {
  FormControl,
  FormLabel,
  Box,
  Flex,
  FlexProps,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import KYDDatePicker from "./KYDDatePicker";
import dayjs, { Dayjs } from "dayjs";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionLabel = motion(FormLabel);

interface KYDLabeledDatePickerProps {
  name: string;
  label: string;
  value: string;
  onChange: (date: Dayjs | null) => void;
  isDisabled?: boolean;
  error?: string;
  containerStyle?: FlexProps;
  dateTimeProps?: any;
}

const KYDLabeledDatePicker = ({
  name,
  label,
  value,
  onChange,
  isDisabled = false,
  error,
  containerStyle,
  dateTimeProps = {},
}: KYDLabeledDatePickerProps) => {
  const [focus, setFocus] = useState(false);
  const hasValue = !!value;
  const active = focus || hasValue;

  return (
    <FormControl isInvalid={!!error} w="100%">
      <MotionBox
        h="50px"
        boxShadow="0px 0px 10px 0px #0000000D"
        border="1px solid #E6E6E6"
        borderRadius="8px"
        p={3}
        py={active ? 1 : 3}
        transition={{ duration: 0.2, ease: "easeOut" }}
        animate={{
          paddingTop: active ? 4 : 12,
          paddingBottom: active ? 4 : 12,
        }}
        {...containerStyle}
      >
        <MotionFlex
          direction={active ? "column" : "row"}
          align={active ? "stretch" : "center"}
          gap={active ? 0 : 2}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          {label && (
            <MotionLabel
              htmlFor={name}
              fontSize={active ? "12px" : "14px"}
              fontWeight={active ? "400" : "500"}
              color="#00000080"
              whiteSpace={active ? "normal" : "nowrap"}
              mb={0}
              mr={1}
              pointerEvents="none"
              display="flex"
              alignItems="center"
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {label}
            </MotionLabel>
          )}

          <Box flex="1" display="flex" alignItems="center" w="100%">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box w="100%">
                <KYDDatePicker
                  value={value}
                  onChange={(date) => {
                    onChange(date);
                  }}
                  name={name}
                  isDisabled={isDisabled}
                  required={false}
                  dateTimeProps={{
                    ...dateTimeProps,
                    slotProps: {
                      ...dateTimeProps.slotProps,
                      actionBar: {
                        actions: ["clear", "accept"],
                        ...dateTimeProps.slotProps?.actionBar,
                      },
                      textField: {
                        onFocus: () => setFocus(true),
                        onBlur: () => setFocus(false),
                        sx: {
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          boxShadow: "none",
                          "& .MuiInputBase-root": {
                            padding: 0,
                            height: "auto",
                          },
                          "& .MuiInputBase-input": {
                            padding: 0,
                            fontFamily: "Helvetica",
                            fontSize: "14px",
                          },
                          ...dateTimeProps.slotProps?.textField?.sx,
                        },
                      },
                    },
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Box>
        </MotionFlex>
      </MotionBox>
    </FormControl>
  );
};

export default KYDLabeledDatePicker;

